import type { CSSProperties } from 'react';
import { Button, Col, Input, Row, Select, Space, Tabs, Typography } from 'antd';
import { BgColorsOutlined, FontSizeOutlined, LinkOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { MAX_IMAGE_SIZE } from '@/constants/files';
import { JsonSchemaField } from '@/models/content-block-schema.model';
import { SupportedLocales } from '@/models/localization.model';
import { mergeGalleryAltsFromHeader } from '../../helpers/galleryBlockHelpers';
import {
  buildDefaultValue,
  getArrayItemFieldMap,
  getArrayOfStringItemsSchema,
  normalizeArrayFieldSchema,
} from '../../helpers/schemaDefaults';
import { fieldPropertyLabelKey } from '../../helpers/contentBlockI18n';
import CategoryIdsField from './CategoryIdsField';
import BlogImageField from './BlogImageField';
import { GalleryRootEditor } from './GalleryRootEditor';
import ProductSkusField from './ProductSkusField';

type TObjectFieldsEditor = {
  /** Block machine name, e.g. `button` — used for layout tweaks */
  blockType?: string;
  properties: Record<string, JsonSchemaField>;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  locales: SupportedLocales[];
};

const BUTTON_FIELD_ORDER = ['text', 'link', 'color'] as const;
const GALLERY_FIELD_ORDER = ['header', 'items'] as const;

const FIELD_SHELL_STYLE: CSSProperties = {
  padding: '12px 14px',
  background: '#ffffff',
  border: '1px solid #e4e9f2',
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
};

function sortButtonProperties(entries: [string, JsonSchemaField][]): [string, JsonSchemaField][] {
  const map = new Map(entries);
  const ordered: [string, JsonSchemaField][] = [];
  BUTTON_FIELD_ORDER.forEach((key) => {
    const sch = map.get(key);
    if (sch) ordered.push([key, sch]);
  });
  entries.forEach(([k, v]) => {
    if (!BUTTON_FIELD_ORDER.includes(k as (typeof BUTTON_FIELD_ORDER)[number])) {
      ordered.push([k, v]);
    }
  });
  return ordered;
}

function sortGalleryProperties(entries: [string, JsonSchemaField][]): [string, JsonSchemaField][] {
  const map = new Map(entries);
  const ordered: [string, JsonSchemaField][] = [];
  GALLERY_FIELD_ORDER.forEach((key) => {
    const sch = map.get(key);
    if (sch) ordered.push([key, sch]);
  });
  entries.forEach(([k, v]) => {
    if (!GALLERY_FIELD_ORDER.includes(k as (typeof GALLERY_FIELD_ORDER)[number])) {
      ordered.push([k, v]);
    }
  });
  return ordered;
}

function fieldIconForButton(key: string) {
  switch (key) {
    case 'text':
      return <FontSizeOutlined />;
    case 'link':
      return <LinkOutlined />;
    case 'color':
      return <BgColorsOutlined />;
    default:
      return null;
  }
}

type TFieldProps = {
  fieldKey: string;
  schema: JsonSchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
  locales: SupportedLocales[];
  blockType?: string;
  galleryHeaderSource?: unknown;
};

const TranslatableStringInput = ({
  value,
  onChange,
  locales,
  maxLength,
  textArea,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  locales: SupportedLocales[];
  maxLength?: number;
  textArea?: boolean;
}) => {
  const map = ((): Record<string, string> => {
    if (typeof value === 'string') {
      const primary = locales[0];
      return locales.reduce<Record<string, string>>((acc, l) => {
        acc[l] = primary !== undefined && l === primary ? value : '';
        return acc;
      }, {});
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, string>;
    }
    return locales.reduce<Record<string, string>>((acc, l) => {
      acc[l] = '';
      return acc;
    }, {});
  })();

  const handle = (locale: SupportedLocales, v: string) => {
    onChange({ ...map, [locale]: v });
  };

  return (
    <Tabs
      items={locales.map((locale) => ({
        key: locale,
        label: locale.toUpperCase(),
        children: textArea ? (
          <Input.TextArea
            value={map[locale] || ''}
            maxLength={maxLength}
            onChange={(e) => handle(locale, e.target.value)}
            rows={4}
          />
        ) : (
          <Input value={map[locale] || ''} maxLength={maxLength} onChange={(e) => handle(locale, e.target.value)} />
        ),
      }))}
    />
  );
};

const TranslatableStringArray = ({
  value,
  onChange,
  locales,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  locales: SupportedLocales[];
}) => {
  const { t } = useTranslation(['contentPages']);
  const map = ((): Record<string, string[]> => {
    if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      const lines = value as string[];
      const primary = locales[0];
      return locales.reduce<Record<string, string[]>>((acc, l) => {
        acc[l] = primary !== undefined && l === primary ? [...lines] : [];
        return acc;
      }, {});
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, string[]>;
    }
    return locales.reduce<Record<string, string[]>>((acc, l) => {
      acc[l] = [];
      return acc;
    }, {});
  })();

  const setLocaleLines = (locale: SupportedLocales, lines: string[]) => {
    onChange({ ...map, [locale]: lines });
  };

  return (
    <Tabs
      items={locales.map((locale) => ({
        key: locale,
        label: locale.toUpperCase(),
        children: (
          <Space direction="vertical" style={{ width: '100%' }}>
            {(map[locale] || []).map((line, idx) => (
              <Space key={idx} style={{ width: '100%' }}>
                <Input
                  value={line}
                  onChange={(e) => {
                    const lines = [...(map[locale] || [])];
                    lines[idx] = e.target.value;
                    setLocaleLines(locale, lines);
                  }}
                />
                <Button
                  type="text"
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => {
                    const lines = (map[locale] || []).filter((_, i) => i !== idx);
                    setLocaleLines(locale, lines);
                  }}
                />
              </Space>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setLocaleLines(locale, [...(map[locale] || []), ''])}
            >
              {t('add_list_line')}
            </Button>
          </Space>
        ),
      }))}
    />
  );
};

export const SchemaFieldRenderer = ({
  fieldKey,
  schema,
  value,
  onChange,
  locales,
  blockType,
  galleryHeaderSource,
}: TFieldProps) => {
  const { t } = useTranslation(['contentPages']);
  const maxMb = MAX_IMAGE_SIZE / 1024 / 1024;

  if (fieldKey === 'image' && schema.type !== 'object' && schema.type !== 'array') {
    const imageRequirements =
      blockType === 'big_banner'
        ? t('big_banner_image_requirements', { maxMb })
        : blockType === 'gallery'
        ? t('gallery_image_requirements', { maxMb })
        : undefined;
    return <BlogImageField value={value} onChange={(v) => onChange(v)} requirementsText={imageRequirements} />;
  }

  if (schema.type === 'string') {
    if (schema.translatable) {
      const useTextArea = fieldKey === 'heading' || (fieldKey === 'text' && (!schema.max || schema.max > 15));
      return (
        <TranslatableStringInput
          value={value}
          onChange={onChange}
          locales={locales}
          maxLength={schema.max}
          textArea={useTextArea}
        />
      );
    }
    if (schema.format === 'url' || fieldKey === 'link') {
      return (
        <Input
          style={{ width: '100%' }}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    return (
      <Input
        value={typeof value === 'string' ? value : ''}
        maxLength={schema.max}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (schema.type === 'object' && schema.properties) {
    const inner =
      typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
    const nestedBlockType = blockType === 'big_banner' && fieldKey === 'button' ? 'button' : blockType;
    return (
      // eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion with ObjectFieldsEditor
      <ObjectFieldsEditor
        blockType={nestedBlockType}
        properties={schema.properties}
        value={inner}
        onChange={(next) => onChange(next)}
        locales={locales}
      />
    );
  }

  const isArrayField =
    schema.type === 'array' ||
    (schema.type === undefined &&
      !!schema.items &&
      getArrayOfStringItemsSchema(normalizeArrayFieldSchema(schema)) !== null);

  if (isArrayField) {
    if (fieldKey === 'category_ids') {
      return <CategoryIdsField value={Array.isArray(value) ? (value as string[]) : []} onChange={onChange} />;
    }
    if (fieldKey === 'uuids' || fieldKey === 'skus') {
      return <ProductSkusField value={value} onChange={(v) => onChange(v)} />;
    }
    const asArray = normalizeArrayFieldSchema(schema);
    const stringItemsSchema = getArrayOfStringItemsSchema(asArray);
    if (stringItemsSchema) {
      if (schema.translatable) {
        return <TranslatableStringArray value={value} onChange={onChange} locales={locales} />;
      }
      return (
        <Select
          mode="tags"
          style={{ width: '100%' }}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(v) => onChange(v)}
        />
      );
    }
    const nestedGalleryItems = getArrayItemFieldMap(asArray);
    if (nestedGalleryItems) {
      return (
        <GalleryRootEditor
          schema={asArray}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          locales={locales}
          maxItems={schema.max_items}
          headerSource={galleryHeaderSource}
        />
      );
    }
  }

  return <Typography.Text type="secondary">{t('inspector_unsupported_field', { field: fieldKey })}</Typography.Text>;
};

export const ObjectFieldsEditor = ({ blockType, properties, value, onChange, locales }: TObjectFieldsEditor) => {
  const { t } = useTranslation(['contentPages']);
  const patch = (key: string, v: unknown) => {
    if (blockType === 'gallery' && key === 'header') {
      const items = value.items;
      const merged = Array.isArray(items) ? mergeGalleryAltsFromHeader(items, v, locales) : items;
      onChange({ ...value, header: v, items: merged });
      return;
    }
    onChange({ ...value, [key]: v });
  };

  const fieldTitle = (key: string) => t(fieldPropertyLabelKey(key), { defaultValue: key });

  const requiredMark = (fieldSchema: JsonSchemaField) =>
    fieldSchema.required === true ? <Typography.Text type="danger"> *</Typography.Text> : null;

  const entries =
    blockType === 'button'
      ? sortButtonProperties(Object.entries(properties))
      : blockType === 'gallery'
      ? sortGalleryProperties(Object.entries(properties))
      : Object.entries(properties);

  const renderOneField = (key: string, fieldSchema: JsonSchemaField) => {
    if (key.startsWith('_')) return null;
    if ((blockType === 'gallery' || blockType === 'big_banner') && key === 'link') return null;

    const fieldVal = value[key];

    if (
      fieldSchema.type === 'object' &&
      fieldSchema.nullable &&
      fieldSchema.properties &&
      (fieldVal === undefined || fieldVal === null)
    ) {
      return (
        <div key={key} style={FIELD_SHELL_STYLE}>
          <Typography.Text strong>
            {fieldTitle(key)}
            {requiredMark(fieldSchema)}
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Button
              type="dashed"
              onClick={() => {
                const def = buildDefaultValue(
                  { type: 'object', properties: fieldSchema.properties },
                  locales,
                ) as Record<string, unknown>;
                patch(key, def);
              }}
            >
              {t('inspector_add_field', { field: fieldTitle(key) })}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div key={key} style={FIELD_SHELL_STYLE}>
        <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
          {blockType === 'button' ? (
            <Space align="center">
              {fieldIconForButton(key)}
              <span>{fieldTitle(key)}</span>
              {requiredMark(fieldSchema)}
            </Space>
          ) : (
            <>
              {fieldTitle(key)}
              {requiredMark(fieldSchema)}
            </>
          )}
        </Typography.Text>
        <SchemaFieldRenderer
          fieldKey={key}
          schema={fieldSchema}
          value={fieldVal}
          onChange={(v) => patch(key, v)}
          locales={locales}
          blockType={blockType}
          galleryHeaderSource={blockType === 'gallery' ? value.header : undefined}
        />
      </div>
    );
  };

  if (blockType === 'button') {
    const textEntry = entries.find(([k]) => k === 'text');
    const linkEntry = entries.find(([k]) => k === 'link');
    const colorEntry = entries.find(([k]) => k === 'color');
    const rest = entries.filter(([k]) => !['text', 'link', 'color'].includes(k));

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {textEntry ? renderOneField(textEntry[0], textEntry[1]) : null}
        {linkEntry || colorEntry ? (
          <Row gutter={[16, 16]} align="top">
            {linkEntry ? <Col xs={24}>{renderOneField(linkEntry[0], linkEntry[1])}</Col> : null}
            {colorEntry ? (
              <Col xs={24} md={12}>
                {renderOneField(colorEntry[0], colorEntry[1])}
              </Col>
            ) : null}
          </Row>
        ) : null}
        {rest.map(([key, fieldSchema]) => renderOneField(key, fieldSchema))}
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {entries.map(([key, fieldSchema]) => renderOneField(key, fieldSchema))}
    </Space>
  );
};
